import { BehaviorSubject, Observable, queueScheduler, Observer, Operator } from 'rxjs';
import { observeOn, scan, distinctUntilChanged, map } from 'rxjs/operators';

export interface Time {
  hour?: string | number;
  minute?: string | number;
  seconds?: string | number;
  isPM?: boolean;
}

export interface TimepickerControls {
  canIncrementHours: boolean;
  canIncrementMinutes: boolean;
  canIncrementSeconds: boolean;

  canDecrementHours: boolean;
  canDecrementMinutes: boolean;
  canDecrementSeconds: boolean;

  canToggleMeridian: boolean;
}

export interface TimepickerComponentState {
  minDate?: Date;
  maxDate?: Date;

  hourStep: number;
  minuteStep: number;
  secondsStep: number;

  readonlyInput: boolean;
  isDisabled: boolean;

  mousewheel: boolean;
  arrowkeys: boolean;

  showSpinners: boolean;
  showMeridian: boolean;
  showSeconds: boolean;

  meridians: string[];
}

export type TimeChangeSource = 'wheel' | 'key' | '';

export interface TimeChangeEvent {
  step: number;
  source: TimeChangeSource;
}

export interface Action {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
}

export type ActionReducer<T> = (state: T, action: Action) => T;

export class MiniState<T> extends BehaviorSubject<T> {
  constructor(
    _initialState: T,
    actionsDispatcher$: Observable<Action>,
    reducer: ActionReducer<T>
  ) {
    super(_initialState);

    const actionInQueue$ = actionsDispatcher$.pipe(
      observeOn(queueScheduler)
    );
    const state$ = actionInQueue$.pipe(
      scan((state: T, action: Action) => {
        if (!action) {
          return state;
        }

        return reducer(state, action);
      },
        _initialState
      ));

    state$.subscribe((value: T) => this.next(value));
  }
}

export class MiniStore<T> extends Observable<T> implements Observer<Action> {
  constructor(
    private _dispatcher: Observer<Action>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _reducer: ActionReducer<any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state$: Observable<any>
  ) {
    super();

    this.source = state$;
  }

  select<R>(pathOrMapFn: (state: T) => R): Observable<R> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const mapped$: Observable<R> = this.source?.pipe(map(pathOrMapFn)) || new Observable().pipe(map(pathOrMapFn));
    return mapped$.pipe(distinctUntilChanged());
  }

  lift<R>(operator: Operator<T, R>): MiniStore<R> {
    const store = new MiniStore<R>(this._dispatcher, this._reducer, this);
    store.operator = operator;

    return store;
  }

  dispatch(action: Action) {
    this._dispatcher.next(action);
  }

  next(action: Action) {
    this._dispatcher.next(action);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error(err: any) {
    this._dispatcher.error(err);
  }

  complete() {
    /*noop*/
  }
}
